import { Box, List, ListItem, ListItemButton, ListItemText, Typography, Divider, Badge, ListItemIcon, IconButton, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, CircularProgress, Collapse, Tooltip } from '@mui/material';
import ContactsIcon from '@mui/icons-material/Contacts';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SettingsIcon from '@mui/icons-material/Settings';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEmailStore } from '@/store/emailStore';
import InboxIcon from '@mui/icons-material/Inbox';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SendIcon from '@mui/icons-material/Send';
import DraftsIcon from '@mui/icons-material/Drafts';
import DeleteIcon from '@mui/icons-material/Delete';
import ArchiveIcon from '@mui/icons-material/Archive';
import StarIcon from '@mui/icons-material/Star';
import OutboxIcon from '@mui/icons-material/Outbox';
import FolderIcon from '@mui/icons-material/Folder';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useState, useMemo, MouseEvent } from 'react';
import { JmapClient } from '@/services/jmapClient';
import { useI18n } from '@/i18n';
import { useToast } from '@/contexts/ToastContext';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';

interface SidebarProps {
  token: string;
  accountId: string;
  jmapClient?: JmapClient;
  /** Show only the 70px module-switcher rail (used inside the persistent app shell). */
  miniOnly?: boolean;
  /** Show only the mailbox-tree column. The mini rail is provided by the shell, so on the mail page we render this variant to avoid a double rail. */
  treeOnly?: boolean;
}

export const Sidebar = ({ accountId, jmapClient, miniOnly = false, treeOnly = false }: SidebarProps) => {
  const {
    mailboxes,
    selectedMailbox,
    selectMailbox,
    setMailboxes,
    setEmails,
    selectEmail,
    emails,
    clearEmailListCache,
    selectedEmail,
    favoriteMailboxIds,
    toggleFavoriteMailbox,
    resetNewMailCount,
    resetNewCalendarCount,
  } = useEmailStore();
  const navigate = useNavigate();
  const location = useLocation();
  const activePath = location.pathname;
  const { t } = useI18n();
  const { showSuccess, showError } = useToast();
  const { dialogProps, confirm } = useConfirmDialog();

  // Folder management state
  const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number; mailboxId: string; mailboxName: string; role: string | null } | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renameFolderName, setRenameFolderName] = useState('');
  const [selectedMailboxForAction, setSelectedMailboxForAction] = useState<string | null>(null);
  const [dragOverMailbox, setDragOverMailbox] = useState<string | null>(null);
  const [folderLoading, setFolderLoading] = useState(false);
  const [favoritesExpanded, setFavoritesExpanded] = useState(true);
  const blockedMoveRoles = new Set(['drafts', 'sent', 'outbox', 'spam']);
  const favoriteMailboxes = mailboxes.filter((mb) => favoriteMailboxIds.includes(mb.id));

  const getMailboxIcon = (role: string | null) => {
    switch (role) {
      case 'inbox':
        return <InboxIcon />;
      case 'sent':
        return <SendIcon />;
      case 'drafts':
        return <DraftsIcon />;
      case 'trash':
        return <DeleteIcon />;
      case 'archive':
        return <ArchiveIcon />;
      case 'outbox':
        return <OutboxIcon />;
      case 'important':
      case 'flagged':
        return <StarIcon />;
      default:
        return <FolderIcon />;
    }
  };

  // Sort mailboxes by sortOrder. Memoized — the parent re-renders on every
  // store update (newMailCount, etc.), and re-sorting on each one shows up
  // as sidebar flicker on big mailbox sets.
  const sortedMailboxes = useMemo(
    () => [...mailboxes].sort((a, b) => a.sortOrder - b.sortOrder),
    [mailboxes]
  );

  // System folders that cannot be renamed or deleted
  const isSystemFolder = (role: string | null) => {
    return !!role; // Only roles that are set are system folders
  };

  const handleContextMenu = (event: MouseEvent, mailbox: any) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
      mailboxId: mailbox.id,
      mailboxName: mailbox.name,
      role: mailbox.role
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleCreateFolder = async () => {
    if (!jmapClient || !newFolderName.trim()) return;

    setFolderLoading(true);

    try {
      await jmapClient.createMailbox(accountId, newFolderName.trim());
      // Refresh mailboxes
      const updatedMailboxes = await jmapClient.getMailboxes(accountId);
      setMailboxes(updatedMailboxes);
      showSuccess(`Folder "${newFolderName.trim()}" created successfully`);
      setNewFolderName('');
      setCreateDialogOpen(false);
    } catch (error) {
      console.error('Failed to create folder:', error);
      showError('Failed to create folder: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setFolderLoading(false);
    }
  };

  const handleRenameFolder = async () => {
    if (!jmapClient || !selectedMailboxForAction || !renameFolderName.trim()) return;

    setFolderLoading(true);

    try {
      await jmapClient.renameMailbox(accountId, selectedMailboxForAction, renameFolderName.trim());
      // Refresh mailboxes
      const updatedMailboxes = await jmapClient.getMailboxes(accountId);
      setMailboxes(updatedMailboxes);
      showSuccess(`Folder renamed to "${renameFolderName.trim()}" successfully`);
      setRenameFolderName('');
      setRenameDialogOpen(false);
      setSelectedMailboxForAction(null);
    } catch (error) {
      console.error('Failed to rename folder:', error);
      showError('Failed to rename folder: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setFolderLoading(false);
    }
  };

  const handleDeleteFolder = async (mailboxId: string, mailboxName: string) => {
    if (!jmapClient) return;

    const confirmed = await confirm({
      title: 'Delete Folder',
      message: `Are you sure you want to delete "${mailboxName}"? This action cannot be undone.`,
      confirmText: 'Delete',
      confirmColor: 'error',
    });

    if (!confirmed) return;

    setFolderLoading(true);

    try {
      await jmapClient.deleteMailbox(accountId, mailboxId);
      // Refresh mailboxes
      const updatedMailboxes = await jmapClient.getMailboxes(accountId);
      setMailboxes(updatedMailboxes);
      showSuccess(`Folder "${mailboxName}" deleted successfully`);
    } catch (error) {
      console.error('Failed to delete folder:', error);
      showError('Failed to delete folder: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setFolderLoading(false);
    }
  };

  const handleEmptyFolder = async (mailboxId: string, mailboxName: string) => {
    if (!jmapClient) return;

    const confirmed = await confirm({
      title: t('sidebar.emptyFolder'),
      message: `${t('sidebar.emptyFolderConfirm')} "${mailboxName}"?`,
      confirmText: t('sidebar.emptyFolderAction'),
      confirmColor: 'error',
    });

    if (!confirmed) return;

    setFolderLoading(true);
    try {
      await jmapClient.emptyMailbox(accountId, mailboxId);
      if (selectedMailbox === mailboxId) {
        setEmails([]);
        selectEmail(null);
      }
      clearEmailListCache();
      const updatedMailboxes = await jmapClient.getMailboxes(accountId);
      setMailboxes(updatedMailboxes);
      showSuccess(`${t('sidebar.emptyFolderSuccess')} "${mailboxName}"`);
    } catch (error) {
      console.error('Failed to empty folder:', error);
      showError('Failed to empty folder: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setFolderLoading(false);
    }
  };

  const handleMarkAllAsRead = async (mailboxId: string, mailboxName: string) => {
    if (!jmapClient) return;

    const confirmed = await confirm({
      title: t('sidebar.markAllRead'),
      message: `${t('sidebar.markAllReadConfirm')} "${mailboxName}"?`,
      confirmText: t('sidebar.markAllReadAction'),
    });

    if (!confirmed) return;

    setFolderLoading(true);
    try {
      await jmapClient.markAllAsRead(accountId, mailboxId);
      if (selectedMailbox === mailboxId && emails.length) {
        setEmails(
          emails.map((email) => ({
            ...email,
            keywords: { ...(email.keywords || {}), '$seen': true },
          }))
        );
      }
      clearEmailListCache();
      setMailboxes(
        mailboxes.map((mb) =>
          mb.id === mailboxId ? { ...mb, unreadEmails: 0 } : mb
        )
      );
      try {
        const updatedMailboxes = await jmapClient.getMailboxes(accountId);
        setMailboxes(updatedMailboxes);
      } catch {}
      showSuccess(`${t('sidebar.markAllReadSuccess')} "${mailboxName}"`);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      showError('Failed to mark all as read: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setFolderLoading(false);
    }
  };

  const handleMenuRename = () => {
    if (contextMenu) {
      setSelectedMailboxForAction(contextMenu.mailboxId);
      setRenameFolderName(contextMenu.mailboxName);
      setRenameDialogOpen(true);
      handleCloseContextMenu();
    }
  };

  const handleMenuDelete = () => {
    if (contextMenu) {
      const { mailboxId, mailboxName } = contextMenu;
      handleCloseContextMenu();
      handleDeleteFolder(mailboxId, mailboxName);
    }
  };

  const handleMenuEmpty = () => {
    if (contextMenu) {
      const { mailboxId, mailboxName } = contextMenu;
      handleCloseContextMenu();
      handleEmptyFolder(mailboxId, mailboxName);
    }
  };

  const handleMenuMarkAllRead = () => {
    if (contextMenu) {
      const { mailboxId, mailboxName } = contextMenu;
      handleCloseContextMenu();
      handleMarkAllAsRead(mailboxId, mailboxName);
    }
  };

  const handleMenuToggleFavorite = () => {
    if (contextMenu) {
      toggleFavoriteMailbox(contextMenu.mailboxId);
      handleCloseContextMenu();
    }
  };

  const getMailboxLabel = (mailbox: any) => {
    if (mailbox?.role === 'inbox') return t('sidebar.inbox');
    return mailbox?.name || '';
  };

  const handleDragOver = (event: React.DragEvent, mailboxId: string) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDragOverMailbox(mailboxId);
  };

  const handleDragLeave = () => {
    setDragOverMailbox(null);
  };

  const handleDrop = async (event: React.DragEvent, targetMailboxId: string) => {
    event.preventDefault();
    setDragOverMailbox(null);

    if (!jmapClient) return;

    try {
      const targetMailbox = mailboxes.find((mb: any) => mb.id === targetMailboxId);
      if (targetMailbox?.role && blockedMoveRoles.has(targetMailbox.role)) {
        showError(t('emailList.errorMoveBlocked'));
        return;
      }
      const data = JSON.parse(event.dataTransfer.getData('application/json'));
      const { emailId, mailboxIds } = data;

      await jmapClient.moveEmail(accountId, emailId, targetMailboxId, mailboxIds);

      if (selectedMailbox && mailboxIds?.[selectedMailbox] && selectedMailbox !== targetMailboxId) {
        setEmails(emails.filter((email) => email.id !== emailId));
        if (selectedEmail === emailId) {
          selectEmail(null);
        }
      }
      clearEmailListCache();

      // Refresh mailboxes to update counts
      const updatedMailboxes = await jmapClient.getMailboxes(accountId);
      setMailboxes(updatedMailboxes);
      showSuccess('Email moved successfully');
    } catch (error) {
      console.error('Failed to move email:', error);
      showError('Failed to move email: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  return (
    <Box
      sx={{
        width: treeOnly ? 230 : miniOnly ? 70 : 300,
        borderRight: 1,
        borderColor: 'divider',
        display: 'flex',
        height: '100%',
        bgcolor: 'background.paper',
      }}
    >
      {/* Mini sidebar — hidden in treeOnly mode (shell already provides it). */}
      {!treeOnly && (
      <Box
        sx={{
          width: 70,
          borderRight: 1,
          borderColor: 'divider',
          bgcolor: (t) => t.palette.mode === 'dark' ? 'background.default' : 'grey.50',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 2,
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title={t('sidebar.title')} placement="right">
            <IconButton
              size="medium"
              onClick={() => { resetNewMailCount(); navigate('/'); }}
              color={activePath === '/' ? 'primary' : 'default'}
              aria-label={t('sidebar.title')}
              sx={{ width: 44, height: 44 }}
            >
              <MailOutlineIcon fontSize="medium" />
            </IconButton>
          </Tooltip>
          <Typography variant="caption" color="text.secondary">
            {t('sidebar.title')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title={t('sidebar.calendar')} placement="right">
            <IconButton
              size="medium"
              onClick={() => { resetNewCalendarCount(); navigate('/calendar'); }}
              color={activePath === '/calendar' ? 'primary' : 'default'}
              aria-label={t('sidebar.calendar')}
              sx={{ width: 44, height: 44 }}
            >
              <CalendarTodayIcon fontSize="medium" />
            </IconButton>
          </Tooltip>
          <Typography variant="caption" color="text.secondary">
            {t('sidebar.calendar')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title={t('sidebar.contacts')} placement="right">
            <IconButton
              size="medium"
              onClick={() => navigate('/contacts')}
              color={activePath === '/contacts' ? 'primary' : 'default'}
              aria-label={t('sidebar.contacts')}
              sx={{ width: 44, height: 44 }}
            >
              <ContactsIcon fontSize="medium" />
            </IconButton>
          </Tooltip>
          <Typography variant="caption" color="text.secondary">
            {t('sidebar.contacts')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title={t('settings.title')} placement="right">
            <IconButton
              size="medium"
              onClick={() => navigate('/settings')}
              color={activePath === '/settings' ? 'primary' : 'default'}
              aria-label={t('settings.title')}
              sx={{ width: 44, height: 44 }}
            >
              <SettingsIcon fontSize="medium" />
            </IconButton>
          </Tooltip>
          <Typography variant="caption" color="text.secondary">
            {t('settings.title')}
          </Typography>
        </Box>
      </Box>
      )}

      {/* Mailbox tree column — shown when not in miniOnly. */}
      {!miniOnly && (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{
          px: 1.5,
          py: '18px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
        }}>
          <Typography variant="h6" sx={{ fontSize: '18px' }}>
            {t('sidebar.title')}
          </Typography>
          {jmapClient && (
            <IconButton
              size="small"
              onClick={() => setCreateDialogOpen(true)}
              title={t('sidebar.createFolder')}
              aria-label={t('sidebar.createFolder')}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          <List>
          {favoriteMailboxes.length > 0 && (
            <>
              <ListItemButton
                onClick={() => setFavoritesExpanded((prev) => !prev)}
                sx={{ mx: 1.5, my: 0.25, borderRadius: 2, '&:hover': { bgcolor: 'action.hover' } }}
              >
                {/* <ListItemIcon sx={{ minWidth: 40 }}>
                  <StarIcon sx={{ color: 'warning.main' }} />
                </ListItemIcon> */}
                <ListItemText
                  primary={t('sidebar.favorites')}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                />
                {favoritesExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </ListItemButton>
              <Collapse in={favoritesExpanded} timeout="auto" unmountOnExit>
                {favoriteMailboxes.map((mailbox) => (
                  <ListItem key={`fav-${mailbox.id}`} disablePadding>
                    <ListItemButton
                      selected={activePath === '/' && selectedMailbox === mailbox.id}
                      onClick={() => {
                        selectMailbox(mailbox.id);
                        navigate('/');
                      }}
                      sx={{ mx: 3, my: 0.25, borderRadius: 2, '&:hover': { bgcolor: 'action.hover' } }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {getMailboxIcon(mailbox.role)}
                      </ListItemIcon>
                      <ListItemText
                        primary={getMailboxLabel(mailbox)}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </Collapse>
              <Divider sx={{ my: 1.5, mx: 2 }} />
            </>
          )}
          {/* Special virtual folder for Starred emails */}
          <ListItem disablePadding>
            <ListItemButton
              selected={activePath === '/' && selectedMailbox === 'starred'}
              onClick={() => {
                selectMailbox('starred');
                navigate('/');
              }}
                sx={{
                  mx: 1.5,
                  my: 0.5,
                  borderRadius: 2,
                  '&.Mui-selected': {
                    bgcolor: (theme) => theme.palette.action.selected,
                    '&:hover': {
                      bgcolor: (theme) => theme.palette.action.selected,
                    },
                  },
                  '&:hover': {
                    bgcolor: (theme) => theme.palette.action.hover,
                  },
                  // ...(dragOverMailbox === mailbox.id && {
                  //   bgcolor: (theme) => theme.palette.action.hover,
                  //   border: '2px dashed',
                  //   borderColor: 'primary.main',
                  //   borderRadius: 1,
                  //   position: 'relative',
                  //   '&::after': {
                  //     content: '"Drop to move here"',
                  //     position: 'absolute',
                  //     right: 12,
                  //     fontSize: '0.75rem',
                  //     color: 'primary.main',
                  //     fontWeight: 600,
                  //     pointerEvents: 'none',
                  //   },
                  //   '&:hover': {
                  //     bgcolor: (theme) => theme.palette.action.hover,
                  //   },
                  // }),
                }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <StarIcon sx={{ color: 'warning.main' }} />
              </ListItemIcon>
              <ListItemText
                primary="Starred"
                primaryTypographyProps={{
                  variant: 'body2',
                }}
              />
            </ListItemButton>
          </ListItem>
          <Divider sx={{ my: 1.5, mx: 2 }} />

          {sortedMailboxes.map((mailbox) => (
            <ListItem
              key={mailbox.id}
              disablePadding
              secondaryAction={
                jmapClient ? (
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={(e) => handleContextMenu(e, mailbox)}
                    aria-label={`folder options for ${mailbox.name}`}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                ) : null
              }
            >
              <ListItemButton
                selected={activePath === '/' && selectedMailbox === mailbox.id}
                onClick={() => {
                  selectMailbox(mailbox.id);
                  // Navigate to mailbox page if not already there
                  navigate('/');
                }}
                onContextMenu={(e) => {
                  if (jmapClient) {
                    handleContextMenu(e, mailbox);
                  }
                }}
                onDragOver={(e) => jmapClient && handleDragOver(e, mailbox.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => jmapClient && handleDrop(e, mailbox.id)}
                sx={{
                  mx: 1.5,
                  my: 0.5,
                  borderRadius: 2,
                  '&.Mui-selected': {
                    bgcolor: (theme) => theme.palette.action.selected,
                    '&:hover': {
                      bgcolor: (theme) => theme.palette.action.selected,
                    },
                  },
                  '&:hover': {
                    bgcolor: (theme) => theme.palette.action.hover,
                  },
                  ...(dragOverMailbox === mailbox.id && {
                    bgcolor: (theme) => theme.palette.action.hover,
                    border: '2px dashed',
                    borderColor: 'primary.main',
                    borderRadius: 1,
                    position: 'relative',
                    '&::after': {
                      content: '"Drop to move here"',
                      position: 'absolute',
                      right: 12,
                      fontSize: '0.75rem',
                      color: 'primary.main',
                      fontWeight: 600,
                      pointerEvents: 'none',
                    },
                    '&:hover': {
                      bgcolor: (theme) => theme.palette.action.hover,
                    },
                  }),
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Badge
                    badgeContent={mailbox.unreadEmails}
                    color="primary"
                    max={999}
                  >
                    {getMailboxIcon(mailbox.role)}
                  </Badge>
                </ListItemIcon>
                <ListItemText
                  primary={getMailboxLabel(mailbox)}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: mailbox.unreadEmails > 0 ? 'medium' : 'regular',
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
          </List>
        </Box>
        </Box>
      )}

      {/* Context Menu for folder actions */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        {contextMenu && favoriteMailboxIds.includes(contextMenu.mailboxId) ? (
          <MenuItem onClick={handleMenuToggleFavorite}>{t('sidebar.removeFromFavorites')}</MenuItem>
        ) : (
          <MenuItem onClick={handleMenuToggleFavorite}>{t('sidebar.addToFavorites')}</MenuItem>
        )}
        {contextMenu && !isSystemFolder(contextMenu.role) && (
          <MenuItem onClick={handleMenuRename}>{t('common.rename')}</MenuItem>
        )}
        {contextMenu && !isSystemFolder(contextMenu.role) && (
          <MenuItem onClick={handleMenuDelete}>{t('common.delete')}</MenuItem>
        )}
        <MenuItem onClick={handleMenuEmpty}>{t('sidebar.emptyFolder')}</MenuItem>
        <MenuItem onClick={handleMenuMarkAllRead}>{t('sidebar.markAllRead')}</MenuItem>
      </Menu>

      {/* Create Folder Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)}>
        <DialogTitle>{t('sidebar.createFolder')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('sidebar.folderName')}
            fullWidth
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !folderLoading) {
                handleCreateFolder();
              }
            }}
            placeholder="e.g., Projects, Archive, Important"
            helperText="Enter a name for your new folder"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={folderLoading}>{t('common.cancel')}</Button>
          <Button onClick={handleCreateFolder} variant="contained" disabled={folderLoading}>
            {folderLoading && <CircularProgress size={20} sx={{ mr: 1 }} />}
            {folderLoading ? 'Creating...' : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename Folder Dialog */}
      <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)}>
        <DialogTitle>{t('sidebar.renameFolder')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('sidebar.newFolderName')}
            fullWidth
            value={renameFolderName}
            onChange={(e) => setRenameFolderName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !folderLoading) {
                handleRenameFolder();
              }
            }}
            placeholder="Enter new folder name"
            helperText="Choose a new name for this folder"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)} disabled={folderLoading}>{t('common.cancel')}</Button>
          <Button onClick={handleRenameFolder} variant="contained" disabled={folderLoading}>
            {folderLoading && <CircularProgress size={20} sx={{ mr: 1 }} />}
            {folderLoading ? 'Renaming...' : t('common.rename')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog {...dialogProps} />
    </Box>
  );
};
